import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IconArrowLeft, IconDownload, IconPrinter, IconCopy, IconRefresh, IconTrash,
  IconLoader2, IconPencil, IconSettings, IconBold, IconItalic, IconUnderline,
  IconAlignLeft, IconAlignCenter, IconAlignRight, IconCheck,
} from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { useAuthStore } from '../../../store/authStore';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { notifications } from '@/lib/notifications';
import aiDocumentService from '../../../services/aiDocumentService';
import LimitReachedModal from '@/components/common/LimitReachedModal';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import BlockEditor, { DocPreviewCanvas, FontSelect, CustomSelect, HtmlDocEditor } from './BlockEditor';
import { toBanglaDigits } from './aiDocShared';
import PdfEditorModal from '../../../features/pdf-editor/PdfEditorModal';
import { layoutToPdf } from '../../../features/pdf-editor/engine/layoutToPdf';
import { docToObjects, objectLayoutToPdf, serializeEditorLayout } from '../../../features/pdf-editor/engine/docToPdf';
import { usePdfEditorStore } from '../../../features/pdf-editor/state/usePdfEditorStore';
import { downloadDocPdf, openPrint, blocksToText, htmlToText, sanitizeDocHtml, PAPER_SIZES } from '../../../utils/aiDocExport';

function Btn({ variant = 'default', size = 'sm', leftIcon, loading, disabled, className = '', children, ...props }) {
  const base = 'inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none';
  const sizes = { sm: 'px-3 py-1.5 text-sm', md: 'px-4 py-2 text-sm' };
  const variants = {
    primary: 'bg-primary hover:bg-primary/90 text-white',
    default: 'bg-white dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-700',
    ghost: 'text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800',
    danger: 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20',
  };
  return (
    <button className={`${base} ${sizes[size]} ${variants[variant]} ${className}`} disabled={disabled || loading} {...props}>
      {loading ? <IconLoader2 size={15} className="animate-spin" /> : leftIcon}
      {children}
    </button>
  );
}

export default function AiDocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, currentLanguage } = useTranslation();
  const { user } = useAuthStore();
  const { selectedOfficeSpace } = useOfficeSpaceStore();
  const isOperator = !!(selectedOfficeSpace && user && selectedOfficeSpace.ownerId != null && selectedOfficeSpace.ownerId !== user.id);
  const isBnUi = currentLanguage === 'bn';

  const [doc, setDoc] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [docHtml, setDocHtml] = useState(null); // HTML-format (form/table) doc; null → block-format
  const [loading, setLoading] = useState(true);
  // Advanced edit — flatten this block/html doc into the shared PDF editor.
  const [advDoc, setAdvDoc] = useState(null);
  const [advOpen, setAdvOpen] = useState(false);
  const [advBuilding, setAdvBuilding] = useState(false);
  const [savedLayout, setSavedLayout] = useState(null);
  const [name, setName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [regenning, setRegenning] = useState(false);
  const [manualEditing, setManualEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [limitOpen, setLimitOpen] = useState(false);
  const [edited, setEdited] = useState(false); // any change → reveal the Save footer
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [pdfing, setPdfing] = useState(false);
  const saveTimer = useRef(null);

  // Document-settings + inline-editing state (mirrors the wizard's step 3).
  const editorRef = useRef(null);
  const [editingIdx, setEditingIdx] = useState(-1);
  const [paper, setPaper] = useState('a4');
  const [landscape, setLandscape] = useState(false);
  const [globalFont, setGlobalFont] = useState(null);
  // Page margins: preset id + custom per-side mm. null margins = the legacy defaults.
  const [marginPreset, setMarginPreset] = useState('default'); // default | narrow | wide | custom
  const [customMm, setCustomMm] = useState({ top: 12.7, bottom: 12.7, left: 14.8, right: 14.8 });
  const margins = useMemo(() => {
    const mm2px = (mm) => Math.round(Math.min(70, Math.max(0, Number(mm) || 0)) * 96 / 25.4);
    if (marginPreset === 'narrow') return { top: mm2px(8), bottom: mm2px(8), left: mm2px(8), right: mm2px(8) };
    if (marginPreset === 'wide') return { top: mm2px(25.4), bottom: mm2px(25.4), left: mm2px(25.4), right: mm2px(25.4) };
    if (marginPreset === 'custom') return { top: mm2px(customMm.top), bottom: mm2px(customMm.bottom), left: mm2px(customMm.left), right: mm2px(customMm.right) };
    return null;
  }, [marginPreset, customMm]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    aiDocumentService
      .get(id)
      .then((res) => {
        if (!alive) return;
        const d = res.data?.document;
        setDoc(d);
        setName(d?.name || '');
        if (d?.layout) {
          // A PDF-edited block/html doc is stored as a geometric-objects layout; the
          // reference-image docs use the 0–1000 grid layout. Open each with its builder.
          const build = d.layout.kind === 'objects'
            ? objectLayoutToPdf(d.layout, d.name || 'document')
            : layoutToPdf(d.layout, { name: d.name || 'document' });
          build
            .then((built) => { if (alive) { setAdvDoc(built); setAdvOpen(true); } })
            .catch(() => {});
          setBlocks([]);
          return;
        }
        // HTML-format (form/table) doc → load into the HTML editor as-is (no bn-digit
        // pass — that would corrupt the markup). Block docs keep the block path.
        const htmlContent = d?.html || (Array.isArray(d?.blocks) && d.blocks[0]?.kind === 'html' ? d.blocks[0].text : null);
        if (d?.format === 'html' || htmlContent != null) {
          setDocHtml(htmlContent || '');
          setBlocks([]);
        } else {
          let b = Array.isArray(d?.blocks) ? d.blocks : [];
          if (d?.language === 'bn') b = b.map((x) => ({ ...x, text: toBanglaDigits(x.text) }));
          setBlocks(b);
        }
      })
      .catch((e) => {
        notifications.show({ message: e.message || t('aiDocuments.notFound', { defaultValue: 'Document not found.' }), color: 'red' });
        navigate('/documents?tab=documents&sub=ai');
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  function onBlockEdit(idx, text, meta) {
    const next = blocks.map((b, i) =>
      i === idx ? { ...b, ...(text !== undefined ? { text } : {}), ...(meta || {}) } : b,
    );
    setBlocks(next);
    setEdited(true);
    if (text !== undefined) setManualEditing(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      aiDocumentService.update(id, { blocks: next }).catch(() => {});
    }, 900);
  }

  // HTML-format inline edit (whole-document contentEditable) — debounced persist.
  function onHtmlEdit(nextHtml) {
    setDocHtml(nextHtml);
    setEdited(true);
    setManualEditing(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      aiDocumentService.update(id, { html: nextHtml }).catch(() => {});
    }, 900);
  }

  async function saveDoc() {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    setSaving(true);
    try {
      await aiDocumentService.update(id, docHtml != null ? { html: editorRef.current?.getHtml?.() ?? docHtml } : { blocks });
      setEdited(false);
      notifications.show({ message: t('aiDocuments.saved', { defaultValue: 'Saved.' }), color: 'green' });
    } catch (e) {
      notifications.show({ message: e.message || t('aiDocuments.saveFailed', { defaultValue: 'Save failed.' }), color: 'red' });
    } finally {
      setSaving(false);
    }
  }

  async function saveName() {
    setEditingName(false);
    const clean = name.trim();
    if (!clean || clean === doc?.name) return;
    try {
      await aiDocumentService.update(id, { name: clean });
      setDoc((d) => ({ ...d, name: clean }));
    } catch (e) {
      notifications.show({ message: e.message || 'Rename failed', color: 'red' });
    }
  }

  async function regenerate() {
    if (manualEditing || regenning || !doc) return;
    setRegenning(true);
    try {
      const activeFields = doc.plan?.fields || [];
      const fieldList = activeFields.map((f) => ({ id: f.id, label: f.label, value: (doc.fields || {})[f.id] || '' }));
      const res = await aiDocumentService.regenerate(id, { fields: doc.fields || {}, fieldList });
      if (res.data?.html != null) {
        setDocHtml(sanitizeDocHtml(res.data.html, { preserveStyles: !!doc?.plan?.preserveStyles }));
      } else {
        let b = res.data?.blocks || [];
        if (doc.language === 'bn') b = b.map((x) => ({ ...x, text: toBanglaDigits(x.text) }));
        setBlocks(b);
      }
      notifications.show({ message: t('aiDocuments.regenerated', { defaultValue: 'Document regenerated.' }), color: 'green' });
    } catch (e) {
      if (e?.code === 'INSUFFICIENT_CREDITS') setLimitOpen(true);
      else notifications.show({ message: e.message || 'Regeneration failed', color: 'red' });
    } finally {
      setRegenning(false);
    }
  }

  async function doDelete() {
    setDeleting(true);
    try {
      await aiDocumentService.remove(id);
      notifications.show({ message: t('aiDocuments.deleted', { defaultValue: 'Document deleted.' }), color: 'green' });
      navigate('/documents?tab=documents&sub=ai');
    } catch (e) {
      notifications.show({ message: e.message || 'Delete failed', color: 'red' });
      setDeleting(false);
      setConfirmDel(false);
    }
  }

  async function copyText() {
    try {
      const text = docHtml != null ? htmlToText(editorRef.current?.getHtml?.() ?? docHtml) : blocksToText(blocks);
      await navigator.clipboard.writeText(text);
      notifications.show({ message: t('aiDocuments.copied', { defaultValue: 'Copied to clipboard.' }), color: 'green' });
    } catch {
      notifications.show({ message: 'Copy failed', color: 'red' });
    }
  }

  const isBnDoc = doc?.language === 'bn';
  const exportOpts = () => ({ isBn: isBnDoc, paper, landscape, globalFont, margins, preserveStyles: !!doc?.plan?.preserveStyles });
  async function handlePrint() {
    if (printing || (!blocks.length && docHtml == null)) return;
    setPrinting(true);
    try {
      if (docHtml != null) await openPrint([], { ...exportOpts(), bodyHtml: editorRef.current?.getHtml?.() ?? docHtml });
      else await openPrint(blocks, exportOpts());
    }
    catch { notifications.show({ message: t('aiDocuments.printFailed', { defaultValue: 'Could not open print.' }), color: 'red' }); }
    finally { setPrinting(false); }
  }
  async function handleDownloadPdf() {
    if (pdfing || (!blocks.length && docHtml == null)) return;
    setPdfing(true);
    try {
      const opts = { filename: doc?.name || 'document', ...exportOpts() };
      if (docHtml != null) await downloadDocPdf([], { ...opts, bodyHtml: editorRef.current?.getHtml?.() ?? docHtml });
      else await downloadDocPdf(blocks, opts);
    }
    catch { notifications.show({ message: t('aiDocuments.pdfFailed2', { defaultValue: 'Could not create the PDF.' }), color: 'red' }); }
    finally { setPdfing(false); }
  }

  // Advanced edit — flatten the current block/html doc into the shared PDF editor.
  async function openAdvancedEdit() {
    if (advBuilding || (!blocks.length && docHtml == null)) return;
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    setAdvBuilding(true);
    try {
      const name = doc?.name || 'document';
      const built = savedLayout
        ? await objectLayoutToPdf(savedLayout, name)
        : await docToObjects({
            name,
            settings: { paper, landscape, globalFontScale: 1, margins, globalFont, isBn: isBnDoc },
            ...(docHtml != null ? { html: editorRef.current?.getHtml?.() ?? docHtml } : { blocks }),
          });
      setAdvDoc(built);
      setAdvOpen(true);
    } catch (e) {
      notifications.show({ message: e?.message || t('aiDocuments.advEditFailed', { defaultValue: 'Could not open the editor.' }), color: 'red' });
    } finally {
      setAdvBuilding(false);
    }
  }

  // Close = leave the editor at once and save the edit in the background (never wait on the network).
  function onAdvancedClose() {
    let layout = null;
    try {
      const st = usePdfEditorStore.getState();
      const snap = serializeEditorLayout(st.pages, st.objectsByUid); // snapshot before the modal unmounts
      if (snap.objects.length) layout = snap;
    } catch { /* nothing to save */ }
    setAdvOpen(false);
    // An editor-only document (the card page below is just a doorway to it) goes back to the AI
    // Documents page when the editor closes; an old HTML / block document stays on its own page.
    if (doc?.layout) navigate('/ai-documents');
    if (layout) {
      setSavedLayout(layout);
      // "Open editor" must show the edit next time, not the original.
      objectLayoutToPdf(layout, doc?.name || 'document').then(setAdvDoc).catch(() => {});
      aiDocumentService.update(id, { layout }).catch((e) => {
        notifications.show({ message: e?.message || t('aiDocuments.advSaveFailed', { defaultValue: 'Could not save the document.' }), color: 'red' });
      });
    }
  }

  async function onAdvancedDone(blob, fname) {
    try {
      const st = usePdfEditorStore.getState();
      const layout = serializeEditorLayout(st.pages, st.objectsByUid);
      if (layout.objects.length) {
        await aiDocumentService.update(id, { layout });
        setSavedLayout(layout);
      }
    } catch (e) {
      notifications.show({ message: e?.message || t('aiDocuments.advSaveFailed', { defaultValue: 'Saved the file, but could not update the document.' }), color: 'red' });
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = /\.pdf$/i.test(fname || '') ? fname : `${fname || 'document'}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    setAdvOpen(false);
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 flex justify-center text-zinc-400">
        <IconLoader2 className="animate-spin" size={28} />
      </div>
    );
  }

  if (doc?.layout) {
    return (
      <div className="max-w-md mx-auto px-4 py-16 text-center space-y-4">
        <p className="text-base font-semibold text-zinc-800 dark:text-zinc-100">{doc.name}</p>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <Btn variant="primary" size="md" onClick={() => setAdvOpen(true)} disabled={!advDoc}>{t('aiDocuments.openEditor', { defaultValue: 'Open editor' })}</Btn>
          <Btn size="md" onClick={() => navigate('/documents?tab=documents&sub=ai')}>{t('aiDocuments.back', { defaultValue: 'Back' })}</Btn>
          <Btn variant="danger" size="md" onClick={() => setConfirmDel(true)}>{t('aiDocuments.delete', { defaultValue: 'Delete' })}</Btn>
        </div>
        {advDoc && (
          <PdfEditorModal
            open={advOpen}
            file={advDoc.file}
            initialObjects={advDoc.objects}
            initialDirty={false}
            documentMode
            onClose={onAdvancedClose}
            onDone={onAdvancedDone}
          />
        )}
        <ConfirmDialog
          open={confirmDel}
          onClose={() => setConfirmDel(false)}
          onConfirm={doDelete}
          loading={deleting}
          title={t('aiDocuments.deleteTitle', { defaultValue: 'Delete this document?' })}
          description={t('aiDocuments.deleteDesc', { defaultValue: 'This cannot be undone.' })}
          confirmLabel={t('aiDocuments.delete', { defaultValue: 'Delete' })}
        />
      </div>
    );
  }

  const docLang = doc?.language === 'bn' ? 'bn' : 'en';
  const toolsOff = editingIdx < 0;
  const activeBlock = editingIdx >= 0 ? blocks[editingIdx] : null;
  const hasDoc = blocks.length > 0 || docHtml != null;

  const segTrack = 'flex items-center gap-1 p-1 rounded-xl bg-zinc-100 dark:bg-zinc-800';
  const segBtn = 'flex-1 inline-flex items-center justify-center h-8 rounded-lg text-xs font-medium transition-colors';
  const segOff = 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200';
  const segOn = 'bg-white dark:bg-zinc-900 text-primary shadow-sm';
  const cardBtn = 'flex flex-col items-center justify-center gap-1.5 h-[68px] px-1 rounded-xl bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-200/70 dark:hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:pointer-events-none';
  const subLabel = 'text-[11px] font-medium text-zinc-400';
  const secLabel = 'text-[13px] font-semibold text-zinc-800 dark:text-zinc-200';

  return (
    <div className="w-full px-3 md:px-6 py-4 md:py-6">
      {/* Header — back + editable name + delete */}
      <div className="flex items-center gap-2 flex-wrap mb-4">
        <Btn variant="ghost" leftIcon={<IconArrowLeft size={16} />} onClick={() => navigate('/documents?tab=documents&sub=ai')}>
          {t('aiDocuments.back', { defaultValue: 'Back' })}
        </Btn>
        <div className="flex items-center gap-1.5 min-w-0">
          {editingName ? (
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => e.key === 'Enter' && saveName()}
              style={{ width: `${Math.min(Math.max((name || '').length + 4, 16), 60)}ch` }}
              className="max-w-[70vw] rounded-md border border-primary/50 bg-white dark:bg-zinc-800 px-2 py-1 text-base md:text-lg font-semibold text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          ) : (
            <button onClick={() => setEditingName(true)} className="group inline-flex items-center gap-1.5 min-w-0">
              <span className="text-base md:text-lg font-semibold text-zinc-900 dark:text-zinc-100 truncate">{doc?.name}</span>
              <IconPencil size={14} className="text-zinc-400 opacity-0 group-hover:opacity-100 shrink-0" />
            </button>
          )}
        </div>
        <div className="ml-auto">
          <button
            type="button"
            onClick={() => setConfirmDel(true)}
            title={t('aiDocuments.delete', { defaultValue: 'Delete' })}
            aria-label={t('aiDocuments.delete', { defaultValue: 'Delete' })}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
          >
            <IconTrash size={16} />
          </button>
        </div>
      </div>

      {/* Two-column: canvas + document-settings sidebar */}
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        <div className="flex-1 min-w-0 w-full">
          <DocPreviewCanvas lang={docLang} name={doc?.name} paper={paper} landscape={landscape}>
            {(zoom) => (docHtml != null ? (
              <HtmlDocEditor
                ref={editorRef}
                html={docHtml}
                onChange={onHtmlEdit}
                onEditingChange={setEditingIdx}
                editable
                lang={docLang}
                zoom={zoom}
                paper={paper}
                landscape={landscape}
                globalFont={globalFont}
                fontScale={1}
                margins={margins}
                preserveStyles={!!doc?.plan?.preserveStyles}
              />
            ) : (
              <BlockEditor
                ref={editorRef}
                blocks={blocks}
                onChange={onBlockEdit}
                onEditingChange={setEditingIdx}
                editable
                lang={docLang}
                zoom={zoom}
                paper={paper}
                landscape={landscape}
                globalFont={globalFont}
                margins={margins}
              />
            ))}
          </DocPreviewCanvas>
        </div>

        {/* Right sidebar — settings-panel style, sticky with a pinned Print footer */}
        <div className="w-full lg:w-72 shrink-0 lg:sticky lg:top-4">
          <div className="rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 overflow-hidden flex flex-col lg:max-h-[calc(100vh-2rem)]">
            <div className="lg:flex-1 lg:overflow-y-auto">
              {/* Header */}
              <div className="flex items-center gap-3 p-4">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary shrink-0">
                  <IconSettings size={20} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100 truncate">
                    {t('aiDocuments.docSettings', { defaultValue: 'Document settings' })}
                  </p>
                  <p className="text-[11px] text-zinc-400 truncate">
                    {t('aiDocuments.docSettingsSub', { defaultValue: 'Page, format & export' })}
                  </p>
                </div>
              </div>

              {/* Page — paper size + document font + orientation */}
              <div className="px-4 pb-4 pt-3 space-y-3 border-t border-zinc-100 dark:border-zinc-800">
                <p className={secLabel}>{t('aiDocuments.pageSettings', { defaultValue: 'Page' })}</p>
                <div className="space-y-1.5">
                  <p className={subLabel}>{t('aiDocuments.paperSize', { defaultValue: 'Paper size' })}</p>
                  <CustomSelect
                    value={paper}
                    onChange={setPaper}
                    className="h-11"
                    options={PAPER_SIZES.map((p) => ({ value: p.id, label: `${p.label} · ${p.dim}` }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <p className={subLabel}>{t('aiDocuments.docFont', { defaultValue: 'Document font' })}</p>
                  <FontSelect value={globalFont || ''} onChange={setGlobalFont} isBn={isBnUi} className="h-11" />
                </div>
                <div className="space-y-1.5">
                  <p className={subLabel}>{t('aiDocuments.orientation', { defaultValue: 'Orientation' })}</p>
                  <div className={segTrack}>
                    <button type="button" className={`${segBtn} ${!landscape ? segOn : segOff}`} onClick={() => setLandscape(false)}>{isBnUi ? 'পোর্ট্রেট' : 'Portrait'}</button>
                    <button type="button" className={`${segBtn} ${landscape ? segOn : segOff}`} onClick={() => setLandscape(true)}>{isBnUi ? 'ল্যান্ডস্কেপ' : 'Landscape'}</button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <p className={subLabel}>{t('aiDocuments.margin', { defaultValue: 'Margins' })}</p>
                  <div className={segTrack}>
                    {[
                      ['default', t('aiDocuments.marginDefault', { defaultValue: 'Default' })],
                      ['narrow', t('aiDocuments.marginNarrow', { defaultValue: 'Narrow' })],
                      ['wide', t('aiDocuments.marginWide', { defaultValue: 'Wide' })],
                      ['custom', t('aiDocuments.marginCustom', { defaultValue: 'Custom' })],
                    ].map(([id, label]) => (
                      <button key={id} type="button" className={`${segBtn} ${marginPreset === id ? segOn : segOff}`} onClick={() => setMarginPreset(id)}>{label}</button>
                    ))}
                  </div>
                  {marginPreset === 'custom' && (
                    <div className="grid grid-cols-4 gap-1.5 pt-1">
                      {[
                        ['top', t('aiDocuments.marginTop', { defaultValue: 'Top' })],
                        ['bottom', t('aiDocuments.marginBottom', { defaultValue: 'Bottom' })],
                        ['left', t('aiDocuments.marginLeft', { defaultValue: 'Left' })],
                        ['right', t('aiDocuments.marginRight', { defaultValue: 'Right' })],
                      ].map(([side, label]) => (
                        <div key={side}>
                          <p className="text-[10px] text-zinc-400 mb-0.5 text-center">{label}</p>
                          <input
                            type="number"
                            min={0}
                            max={70}
                            step={1}
                            value={customMm[side]}
                            onChange={(e) => setCustomMm((m) => ({ ...m, [side]: e.target.value === '' ? '' : Math.min(70, Math.max(0, Number(e.target.value))) }))}
                            className="w-full h-9 px-1 text-center border border-zinc-200 dark:border-zinc-700 rounded-lg text-xs bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                          />
                        </div>
                      ))}
                      <p className="col-span-4 text-[10px] text-zinc-400">{t('aiDocuments.marginUnit', { defaultValue: 'Values in mm' })}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Formatting — shown only while a text/paragraph is selected */}
              {!toolsOff ? (
                <div className="px-4 pb-4 pt-3 space-y-3 border-t border-zinc-100 dark:border-zinc-800">
                  <div className="flex items-center justify-between">
                    <p className={secLabel}>{t('aiDocuments.formatting', { defaultValue: 'Formatting' })}</p>
                    <button
                      type="button"
                      className="text-[11px] font-medium text-primary hover:underline"
                      onMouseDown={(e) => { e.preventDefault(); editorRef.current?.resetMeta(); }}
                    >
                      {t('aiDocuments.reset', { defaultValue: 'Reset' })}
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    <p className={subLabel}>{t('aiDocuments.styleLabel', { defaultValue: 'Style' })}</p>
                    <div className={segTrack}>
                      <button type="button" title="Bold (Ctrl+B)" className={`${segBtn} ${segOff}`} onMouseDown={(e) => { e.preventDefault(); editorRef.current?.exec('bold'); }}><IconBold size={15} /></button>
                      <button type="button" title="Italic (Ctrl+I)" className={`${segBtn} ${segOff}`} onMouseDown={(e) => { e.preventDefault(); editorRef.current?.exec('italic'); }}><IconItalic size={15} /></button>
                      <button type="button" title="Underline (Ctrl+U)" className={`${segBtn} ${segOff}`} onMouseDown={(e) => { e.preventDefault(); editorRef.current?.exec('underline'); }}><IconUnderline size={15} /></button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <p className={subLabel}>{t('aiDocuments.alignment', { defaultValue: 'Alignment' })}</p>
                    <div className={segTrack}>
                      {[
                        { a: 'left', Icon: IconAlignLeft },
                        { a: 'center', Icon: IconAlignCenter },
                        { a: 'right', Icon: IconAlignRight },
                      ].map(({ a, Icon }) => (
                        <button
                          key={a}
                          type="button"
                          title={a}
                          className={`${segBtn} ${activeBlock?.align === a ? segOn : segOff}`}
                          onMouseDown={(e) => { e.preventDefault(); editorRef.current?.setAlign(a); }}
                        >
                          <Icon size={15} />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <p className={subLabel}>{t('aiDocuments.font', { defaultValue: 'Font' })}</p>
                    <FontSelect value={activeBlock?.font || ''} onChange={(id) => editorRef.current?.setFont(id)} isBn={isBnUi} className="h-11" />
                  </div>
                  <p className="text-[11px] text-zinc-400">
                    {t('aiDocuments.editingHint', { defaultValue: 'Formatting applies to the selected text/paragraph.' })}
                  </p>
                </div>
              ) : (
                <div className="px-4 pb-4 pt-3 border-t border-zinc-100 dark:border-zinc-800">
                  <p className={`${secLabel} mb-1.5`}>{t('aiDocuments.formatting', { defaultValue: 'Formatting' })}</p>
                  <p className="text-[11px] text-zinc-400">
                    {t('aiDocuments.clickToEdit', { defaultValue: 'Click any text in the document to edit it.' })}
                  </p>
                </div>
              )}

              {/* Actions — Regenerate / Copy / PDF (Print is the pinned footer) */}
              <div className="px-4 pb-4 pt-3 space-y-2.5 border-t border-zinc-100 dark:border-zinc-800">
                <p className={secLabel}>{t('aiDocuments.actions', { defaultValue: 'Actions' })}</p>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    className={cardBtn}
                    onClick={regenerate}
                    disabled={manualEditing || regenning || !hasDoc}
                    title={manualEditing ? t('aiDocuments.regenLocked', { defaultValue: 'Edited by hand — regeneration disabled' }) : undefined}
                  >
                    {regenning ? <IconLoader2 size={18} className="animate-spin" /> : <IconRefresh size={18} />}
                    <span className="text-[10px] font-medium text-center leading-tight">{t('aiDocuments.regenerate', { defaultValue: 'Regenerate' })}</span>
                  </button>
                  <button type="button" className={cardBtn} onClick={copyText} disabled={!hasDoc}>
                    <IconCopy size={18} />
                    <span className="text-[10px] font-medium text-center leading-tight">{t('aiDocuments.copy', { defaultValue: 'Copy' })}</span>
                  </button>
                  <button
                    type="button"
                    className={cardBtn}
                    onClick={handleDownloadPdf}
                    disabled={!hasDoc || pdfing}
                  >
                    {pdfing ? <IconLoader2 size={18} className="animate-spin" /> : <IconDownload size={18} />}
                    <span className="text-[10px] font-medium text-center leading-tight">{t('aiDocuments.downloadPdf', { defaultValue: 'Download PDF' })}</span>
                  </button>
                </div>
                {/* Advanced edit — open this document in the shared PDF editor for precise layout. */}
                <button
                  type="button"
                  className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-xl text-[12px] font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-200 hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-60 disabled:pointer-events-none"
                  onClick={openAdvancedEdit}
                  disabled={!hasDoc || advBuilding}
                >
                  {advBuilding ? <IconLoader2 size={16} className="animate-spin" /> : <IconPencil size={16} />}
                  {t('aiDocuments.advancedEdit', { defaultValue: 'Advanced edit (PDF editor)' })}
                </button>
              </div>
            </div>{/* /scroll region */}

            {/* Pinned Print button — green, always visible without scrolling */}
            <div className="shrink-0 p-3 border-t border-zinc-100 dark:border-zinc-800 bg-white dark:bg-zinc-900">
              <button
                type="button"
                className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-semibold bg-[#34A853] hover:bg-[#2C9247] text-white shadow-sm transition-colors disabled:opacity-60 disabled:pointer-events-none"
                onClick={handlePrint}
                disabled={!hasDoc || printing}
              >
                {printing ? <IconLoader2 size={16} className="animate-spin" /> : <IconPrinter size={16} />}
                {printing ? t('aiDocuments.preparing', { defaultValue: 'Preparing…' }) : t('aiDocuments.print', { defaultValue: 'Print' })}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Save footer — appears only after an edit (mirrors the wizard's step 3) */}
      {edited && (
        <div className="sticky bottom-3 z-20 mt-3 rounded-2xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-lg px-4 py-3 flex items-center justify-between">
          <span className="text-sm text-zinc-500 dark:text-zinc-400">
            {t('aiDocuments.unsavedChanges', { defaultValue: 'You have unsaved changes.' })}
          </span>
          <Btn variant="primary" size="md" leftIcon={<IconCheck size={16} />} onClick={saveDoc} loading={saving}>
            {t('aiDocuments.saveDoc', { defaultValue: 'Save' })}
          </Btn>
        </div>
      )}

      <ConfirmDialog
        open={confirmDel}
        onClose={() => setConfirmDel(false)}
        onConfirm={doDelete}
        loading={deleting}
        title={t('aiDocuments.deleteTitle', { defaultValue: 'Delete this document?' })}
        description={t('aiDocuments.deleteDesc', { defaultValue: 'This cannot be undone.' })}
        confirmLabel={t('aiDocuments.delete', { defaultValue: 'Delete' })}
      />
      <LimitReachedModal opened={limitOpen} onClose={() => setLimitOpen(false)} isOperator={isOperator} />
      {advDoc && (
        <PdfEditorModal
          open={advOpen}
          file={advDoc.file}
          initialObjects={advDoc.objects}
          initialDirty={false}
          documentMode
          onClose={onAdvancedClose}
          onDone={onAdvancedDone}
        />
      )}
    </div>
  );
}
